import { useCallback } from 'react'
import { type TranslationValues, useTranslation } from 'i18nano'
import type { TranslationKeys, TranslationPaths } from 'shared/lib/i18n'

type Join<K, P> = K extends string | number
	? P extends string | number
		? `${K}${'' extends P ? '' : '.'}${P}`
		: never
	: never

type Paths<T, D extends number = 10> = [D] extends [never]
	? never
	: T extends object
	? {
			[K in keyof T]-?: K extends string | number
				? T[K] extends object
					? Join<K, Paths<T[K], [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9][D]>>
					: `${K}`
				: never
	  }[keyof T]
	: ''

export type TranslationFunction<P extends TranslationPaths | undefined> = (
	path: P extends TranslationPaths
		? keyof TranslationKeys[P]
		: Paths<TranslationKeys>,
	values?: TranslationValues
) => string
/** A full key of one translated string, e.g. `nav.parcels`. */
export type TranslationKey = Paths<TranslationKeys>
export type ColumnTranslationFunction = (
	path: string, // the key under `columns.`
	values?: TranslationValues
) => string

type Translate<P extends TranslationPaths | undefined> = {
	t: TranslationFunction<undefined>
	columnT: ColumnTranslationFunction
	prefixT: TranslationFunction<P>
}

export const useTranslate = <P extends TranslationPaths | undefined>(
	prefix?: P
): Translate<P> => {
	const translation = useTranslation()

	const t = useCallback(
		(path: string, values?: TranslationValues) => translation(path, values),
		[translation]
	)

	const columnT = useCallback(
		(path: string, values?: TranslationValues) =>
			translation(`columns.${path}`, values),
		[translation]
	)

	const prefixT = useCallback(
		(path: string, values?: TranslationValues) =>
			translation(`${prefix || ''}.${path}`, values),
		[translation, prefix]
	)

	return { t, columnT, prefixT }
}
